// SETTINGS V2 — Doprava (Shipping) form model + pure helpers — SETTINGS_V2_SP9
// §2/§4/§6/§7. No React, no moment. Holds the controlled-state shape, the seed /
// validate / graph-rebuild pure functions (kept out of the dialog to stay under
// the 300-line rule), plus the FIX #2 i18n label binders (the legacy EN-hardcoded
// shippingCode() / regionName() helpers are NOT called by v2).
import type { TFunction } from "i18next";

import { CurrencyCode } from "../../../../common/models/CommonModels";
import { country } from "../../../../shared/models/countryModels";
import { DayOfWeek, ShippingOptionCode, ShippingRegionCode } from "../../../../shared/models/models";
import {
  ChangeShippingOptions,
  ShippingOptionInfo,
  ShippingRegionOption,
} from "../../../shared/models/customerShippingOptions";

// ── Controlled-state model (§2 — nested type×region grid of string rows) ──
export interface Row {
  isActive: boolean;
  from: string;
  to: string;
  price: string;
}

export const TYPES = ["standart", "express"] as const;
export const REGIONS = ["country", "europeanUnion", "worldwide"] as const;
export type ShipType = (typeof TYPES)[number];
export type ShipRegion = (typeof REGIONS)[number];

export const REGION_CODE: Record<ShipRegion, ShippingRegionCode> = {
  country: ShippingRegionCode.country,
  europeanUnion: ShippingRegionCode.EU,
  worldwide: ShippingRegionCode.worldwide,
};

export interface ShippingForm {
  standart: Record<ShipRegion, Row>;
  express: Record<ShipRegion, Row>;
  isSelfPickUpActive: boolean;
}

export const emptyRow = (): Row => ({ isActive: false, from: "", to: "", price: "" });

const emptyType = (): Record<ShipRegion, Row> => ({
  country: emptyRow(),
  europeanUnion: emptyRow(),
  worldwide: emptyRow(),
});

export const EMPTY_FORM: ShippingForm = {
  standart: emptyType(),
  express: emptyType(),
  isSelfPickUpActive: false,
};

/** Seed one Row from a returned option (legacy ShippingDialog defaultValues 66-95);
 *  missing option ⇒ empty inactive row (parity with legacy `undefined` defaults). */
export const seedRow = (opt?: ShippingRegionOption): Row => ({
  isActive: opt?.isActive ?? false,
  from: opt ? String(opt.fromBusinessDays) : "",
  to: opt ? String(opt.toBusinessDays) : "",
  price: opt?.orderPrice?.value != null ? String(opt.orderPrice.value) : "",
});

/** Seed the whole controlled form from the current options (§6). */
export const seedForm = (opts: ShippingRegionOption[]): ShippingForm => {
  const find = (tc: ShippingOptionCode, rc: ShippingRegionCode) =>
    opts.filter((x) => x.typeCode === tc).find((x) => x.regionCode === rc);
  const seedType = (tc: ShippingOptionCode): Record<ShipRegion, Row> => ({
    country: seedRow(find(tc, ShippingRegionCode.country)),
    europeanUnion: seedRow(find(tc, ShippingRegionCode.EU)),
    worldwide: seedRow(find(tc, ShippingRegionCode.worldwide)),
  });
  return {
    standart: seedType(ShippingOptionCode.standart),
    express: seedType(ShippingOptionCode.express),
    // Legacy find-first-ACTIVE quirk (§6) — no active pickup option ⇒ false.
    isSelfPickUpActive:
      opts
        .filter((x) => x.typeCode === ShippingOptionCode.selfPickUp)
        .find((x) => x.isActive)?.isActive ?? false,
  };
};

const buildRow = (row: Row): ShippingOptionInfo => ({
  fromBusinessDays: Number(row.from) || 0,
  toBusinessDays: Number(row.to) || 0,
  orderPrice: { id: CurrencyCode.eur, value: Number(row.price || 0) }, // id ALWAYS eur=978
  isActive: row.isActive || false,
});

/** Rebuild the fixed 2-type × 3-region write graph (§2/§7). Every node present
 *  regardless of activity; isSelfPickUpActive forced false without a warehouse. */
export const buildShippingGraph = (
  f: ShippingForm,
  hasWarehouse: boolean
): ChangeShippingOptions => ({
  standartShippingOption: {
    country: buildRow(f.standart.country),
    europeanUnion: buildRow(f.standart.europeanUnion),
    worldwide: buildRow(f.standart.worldwide),
  },
  expressShippingOption: {
    country: buildRow(f.express.country),
    europeanUnion: buildRow(f.express.europeanUnion),
    worldwide: buildRow(f.express.worldwide),
  },
  isSelfPickUpActive: hasWarehouse ? f.isSelfPickUpActive : false,
});

/** Per-active-row validation (§7 — inactive rows skip all checks). Keyed
 *  `${type}.${region}.${field}` for per-field error display. */
export const validateShippingForm = (
  f: ShippingForm,
  t: TFunction
): Record<string, string> => {
  const errs: Record<string, string> = {};
  TYPES.forEach((type) => {
    REGIONS.forEach((region) => {
      const row = f[type][region];
      if (!row.isActive) return;
      const k = `${type}.${region}`;
      if (row.from === "") errs[`${k}.from`] = t("common:forms.required");
      else {
        const n = Number(row.from);
        if (n < 0) errs[`${k}.from`] = t("settings:minValue0");
        else if (n > 365) errs[`${k}.from`] = t("settings:maxValue365");
      }
      if (row.to === "") errs[`${k}.to`] = t("common:forms.required");
      else {
        const n = Number(row.to);
        if (n < 0) errs[`${k}.to`] = t("settings:minValue0");
        else if (n > 365) errs[`${k}.to`] = t("settings:maxValue365");
        else if (row.from !== "" && n < Number(row.from))
          errs[`${k}.to`] = t("settings:mustBeGreaterThan", { minPeriod: row.from });
      }
      if (row.price === "") errs[`${k}.price`] = t("common:forms.required");
      else {
        const n = Number(row.price);
        if (n < 0) errs[`${k}.price`] = t("settings:minValue0");
        else if (n > 10000) errs[`${k}.price`] = t("settings:maxValue10000");
      }
    });
  });
  return errs;
};

/** ".NET/backend time ("16:00:00") → "HH:mm"". Mechanical slice, no moment. */
export const formatHHmm = (t?: string | null): string => {
  if (!t) return "";
  const parts = t.split(":");
  if (parts.length < 2) return t;
  return `${parts[0]}:${parts[1]}`;
};

/** DayOfWeek → the settings short-day i18n key (translated "Mon…" / "Po…").
 *  Keys ship in all 3 langs (en/sk/cs settings.json). */
const SHORT_DAY_KEY: Record<DayOfWeek, string> = {
  [DayOfWeek.monday]: "settings:mon",
  [DayOfWeek.tuesday]: "settings:tue",
  [DayOfWeek.wednesday]: "settings:wed",
  [DayOfWeek.thursday]: "settings:thu",
  [DayOfWeek.friday]: "settings:fri",
  [DayOfWeek.saturday]: "settings:sat",
  [DayOfWeek.sunday]: "settings:sun",
};

/** SP10 F2 — localized day-range compression. Same range-compression algorithm
 *  as the shared compressWeekDays (models.ts), but the day abbreviations come
 *  from the translated settings:mon…sun keys instead of the always-English
 *  substr(0,3). v2-local by design: shared models.ts stays English so its legacy
 *  + supplier-tab consumers are untouched (zero blast radius). */
export const compressWeekDaysLocalized = (
  days: DayOfWeek[],
  t: TFunction
): string => {
  if (!days.length) return "";
  const abbr = (d: DayOfWeek): string => t(SHORT_DAY_KEY[d]);
  const sorted = [...days].sort((a, b) => a - b);
  const parts: string[] = [];
  let start = sorted[0];
  let end = sorted[0];
  const flush = () =>
    parts.push(start === end ? abbr(start) : `${abbr(start)}-${abbr(end)}`);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      flush();
      start = sorted[i];
      end = sorted[i];
    }
  }
  flush();
  return parts.join(", ");
};

/** FIX #2a — method heading i18n (EN-hardcoded shippingCode() unused). */
export const methodLabel = (code: ShippingOptionCode, t: TFunction): string => {
  switch (code) {
    case ShippingOptionCode.standart:
      return t("settings:standart");
    case ShippingOptionCode.express:
      return t("common:express");
    default:
      return t("settings:pickUp");
  }
};

/** FIX #2b — region label i18n. Country name still from country(countryId)?.name
 *  (EN ISO names — accepted residual §2); undefined country ⇒ graceful fallback
 *  key instead of the legacy `'Within undefined'` hazard. */
export const regionLabel = (
  regionCode: ShippingRegionCode,
  countryId: number,
  t: TFunction
): string => {
  switch (regionCode) {
    case ShippingRegionCode.country: {
      const name = country(countryId)?.name;
      return name
        ? t("settings:shipping.regionCountry", { country: name })
        : t("settings:shipping.regionCountryFallback");
    }
    case ShippingRegionCode.EU:
      return t("settings:shipping.regionEU");
    default:
      return t("settings:shipping.regionWorldwide");
  }
};
